/**
 * Sterne-Utilities für CherryPulse
 *
 * Hilfsfunktionen für das 0-5 Sterne-Bewertungssystem
 */

import type {
  StarRating,
  RatingScores,
  EvaluationLevel,
  AgencyLevelScores,
  TeamLevelScores,
  ChemistryLevelScores,
  LonglistLevelScores,
  LonglistCriterionId,
  AgencyMainCriterionId,
  TeamMainCriterionId,
  ChemistryMainCriterionId,
} from '@/types/rating.types';
import { LEVEL_WEIGHTS } from '@/types/rating.types';

// ============================================================================
// CRITERION SCORE CALCULATION
// ============================================================================

/**
 * Berechnet den Score für ein einzelnes Hauptkriterium
 * Wenn Unterkriterien bewertet wurden, wird deren Durchschnitt verwendet
 * Sonst wird der direkte Hauptkriterium-Score verwendet
 */
export function calculateCriterionScore(
  mainScore: StarRating | undefined,
  subScores: (StarRating | undefined)[]
): number | null {
  // Filtere gültige Unterkriterien-Bewertungen (> 0)
  const validSubScores = subScores.filter(
    (s): s is StarRating => s !== undefined && s !== null && s > 0
  );

  if (validSubScores.length > 0) {
    // Unterkriterien-Durchschnitt überschreibt Hauptbewertung
    const sum = validSubScores.reduce<number>((acc, s) => acc + s, 0);
    return sum / validSubScores.length;
  }

  // Wenn keine Unterkriterien, verwende Hauptbewertung
  if (mainScore !== undefined && mainScore !== null && mainScore > 0) {
    return mainScore;
  }

  return null;
}

// ============================================================================
// LEVEL SCORE CALCULATION
// ============================================================================

/**
 * Berechnet den Score für die Agenturebene
 */
export function calculateAgencyLevelScore(scores: AgencyLevelScores): number | null {
  const mainCriteria: AgencyMainCriterionId[] = [
    'leadAgencySuitability',
    'strategicCompetence',
    'workQuality',
    'marketUnderstanding',
    'internationalExperience',
    'taskFit',
  ];

  const criterionScores = mainCriteria
    .map((id) => scores.main[id])
    .filter((v): v is StarRating => v !== undefined && v > 0) as number[];

  if (criterionScores.length === 0) return null;
  return criterionScores.reduce((acc, s) => acc + s, 0) / criterionScores.length;
}

/**
 * Berechnet den Score für die Teamebene
 */
export function calculateTeamLevelScore(scores: TeamLevelScores): number | null {
  const mainCriteria: TeamMainCriterionId[] = [
    'strategyCompetence',
    'creativeCompetence',
    'targetedCreativity',
    'accountCompetence',
    'consultingCompetence',
    'teamDynamics',
    'projectManagement',
    'discussionQuality',
    'proactivity',
  ];

  const criterionScores = mainCriteria
    .map((id) => scores.main[id])
    .filter((v): v is StarRating => v !== undefined && v > 0) as number[];

  if (criterionScores.length === 0) return null;
  return criterionScores.reduce((acc, s) => acc + s, 0) / criterionScores.length;
}

/**
 * Berechnet den Score für die Chemie-Ebene
 */
export function calculateChemistryLevelScore(scores: ChemistryLevelScores): number | null {
  const mainCriteria: ChemistryMainCriterionId[] = [
    'teamFit',
    'passionEnthusiasm',
    'communicationStyle',
    'chemistry',
    'reliability',
    'feedbackOpenness',
  ];

  const criterionScores = mainCriteria
    .map((id) => scores.main[id])
    .filter((v): v is StarRating => v !== undefined && v > 0) as number[];

  if (criterionScores.length === 0) return null;
  return criterionScores.reduce((acc, s) => acc + s, 0) / criterionScores.length;
}

/**
 * Berechnet den Score für eine beliebige Ebene
 */
export function calculateLevelScore(
  level: EvaluationLevel,
  scores: RatingScores
): number | null {
  switch (level) {
    case 'agency':
      return calculateAgencyLevelScore(scores.agency);
    case 'team':
      return calculateTeamLevelScore(scores.team);
    case 'chemistry':
      return calculateChemistryLevelScore(scores.chemistry);
    case 'longlist':
      return scores.longlist ? calculateLonglistScore(scores.longlist) : null;
  }
}

/**
 * Berechnet den Score für die Longlist-Bewertung
 */
export function calculateLonglistScore(scores: LonglistLevelScores): number | null {
  const criteria: LonglistCriterionId[] = [
    'longlistStrategy', 'longlistCreativity', 'longlistDigital',
    'longlistMarket', 'longlistCaseFit', 'longlistTeam', 'longlistMotivation',
  ];

  const rated = criteria
    .map((id) => scores.main[id])
    .filter((v): v is StarRating => v !== undefined && v > 0);

  if (rated.length === 0) return null;
  return rated.reduce<number>((acc, s) => acc + s, 0) / rated.length;
}

// ============================================================================
// CHERRYSCORE CALCULATION
// ============================================================================

/**
 * Berechnet den CherryScore aus allen drei Ebenen
 * Gewichtung: Je 33% (gleichgewichtet)
 *
 * @returns CherryScore (0-5) oder null wenn keine Bewertungen vorhanden
 */
export function calculateCherryScore(scores: RatingScores): number | null {
  // If longlist scores present, use those instead of standard 3-level
  if (scores.longlist) {
    const longlistScore = calculateLonglistScore(scores.longlist);
    if (longlistScore !== null) return longlistScore;
  }

  const levels: EvaluationLevel[] = ['agency', 'team', 'chemistry'];
  const levelScores: { level: EvaluationLevel; score: number; weight: number }[] = [];

  for (const level of levels) {
    const score = calculateLevelScore(level, scores);
    if (score !== null) {
      levelScores.push({
        level,
        score,
        weight: LEVEL_WEIGHTS[level],
      });
    }
  }

  if (levelScores.length === 0) return null;

  // Gewichteter Durchschnitt
  const totalWeight = levelScores.reduce((acc, l) => acc + l.weight, 0);
  const weightedSum = levelScores.reduce((acc, l) => acc + l.score * l.weight, 0);

  // Auf 1 Dezimalstelle runden
  return Math.round((weightedSum / totalWeight) * 10) / 10;
}

// ============================================================================
// EMPTY SCORES FACTORY
// ============================================================================

/**
 * Erstellt ein leeres RatingScores-Objekt
 */
export function createEmptyScores(): RatingScores {
  return {
    agency: {
      main: {},
    },
    team: {
      main: {},
    },
    chemistry: {
      main: {},
    },
  };
}

